/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  MeetingSessionConfiguration,
  ConsoleLogger,
  LogLevel,
  DefaultDeviceController,
  AudioVideoObserver,
  VideoPriorityBasedPolicy,
  VideoSource,
} from 'amazon-chime-sdk-js';
import {
  useLocalVideo,
  useMeetingManager,
  AudioVideoContext,
  useAudioVideo,
  UserActivityProvider,
  useContentShareControls,
  ContentShare,
  useToggleLocalMute,
  LocalVideo,
  VideoGrid,
  RemoteVideos,
  useRemoteVideoTileState,
  useContentShareState,
  useBackgroundBlur,
} from 'amazon-chime-sdk-component-library-react';
import { useNavigate } from 'react-router-dom';
import {
  leaveSessionNewEndPoint,
  therapistEndSession,
  therapistStartSessionNewEndPoint,
} from 'src/api/therapySession';
import {
  getClientIsActiveInSession,
  getTherapistIsActiveInSession,
  getChimeJoinData,
  getActiveRunningSession,
} from 'src/redux/services/teleTherapyServices';
import {
  clearSession,
  setActiveRunningSessionData,
  setChimeSessionData,
  setJoinData,
  setAttendees,
  setVideoDevice,
  setMicDevice,
} from 'src/redux/actions/teleTherapyActions';
import NoRemoteVideoView from './NoRemoteVideoView';
import IconsTray from '../components/icons-tray/IconsTray';
import SettingsModal2 from '../settingsModal/SettingsModal2';
import './meeting-room.scss';
import { Logger } from 'src/latest/src/services';
import {
  Attendee,
  ActiveAttendee,
  ActiveRunningSessionData,
} from './interface';
import Storage from 'src/latest/src/utils/Storage';
import { UserTypes } from 'src/latest/src/constants';
import {
  toggleBackgroundBlur as toggleBackgroundBlurUtil,
  handleSetVideoResolution as handleSetVideoResolutionUtil,
  updateDownlinkPreference as updateDownlinkPreferenceUtil,
  startMeeting as startMeetingUtil,
  toggleContentShareCustom as toggleContentShareCustomUtil,
} from './utils/meetingUtils';
import PermissionModal from '../components/PermissionModal';
import { meetingDevicePreference } from './constant';
import { DevicePermission } from 'src/legacy/utils';
import { useIsMobile } from 'src/latest/src/hooks';
import MicrophoneOff from '../../../../../assets/icons/icon-microphone-white.svg';

interface MetricsData {
  availableOutgoingBitrate: number;
  audioUpstreamJitterMs: number;
  audioPacketLossPercent: number;
}

const MeetingRoom: React.FC = () => {
  const { loggedInUser } = useSelector((state: any) => state.authentication);
  const dispatch = useDispatch();
  const deviceControllerRef = useRef<DefaultDeviceController | null>(null);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  // TODO: Kept it for future use
  // const [attendeeInfo, setAttendeeInfo] = useState(null);

  const [meetingInfo, setMeetingInfo] = useState(null);

  // TODO: Kept it for future use
  // const [metricsInfo, setMetricsInfo] = useState({});
  const metricsRef = useRef<MetricsData>({
    availableOutgoingBitrate: 0,
    audioUpstreamJitterMs: 0,
    audioPacketLossPercent: 0,
  });

  const {
    attendees,
    chime_data,
    joinData,
    isMicEnabled,
    isVideoEnabledFlag,
    micDevice,
    videoDevice,
    showSelfView,
    localVideoResolution,
    blurBackground,
    activeRunningSessionData,
  } = useSelector((state: any) => state.teleTherapy);

  const meetingManager = useMeetingManager();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const { muted, toggleMute } = useToggleLocalMute();
  const audioVideo = useAudioVideo();
  const { toggleContentShare } = useContentShareControls();
  const navigate = useNavigate();
  const { isLocalUserSharing, sharingAttendeeId } = useContentShareState();
  const isTherapist = loggedInUser?.user_type?.includes('THERAPIST');
  const { tiles } = useRemoteVideoTileState();
  const gridSize = isVideoEnabled ? tiles.length : tiles.length;
  const { isBackgroundBlurSupported, createBackgroundBlurDevice } =
    useBackgroundBlur();

  const [showModal, setShowModal] = useState(false);
  const [activeAttendees, setActiveAttendees] = useState<ActiveAttendee[]>([]);
  const [remoteMuteMap, setRemoteMuteMap] = useState<Record<string, boolean>>(
    {},
  );
  // true if any *other* attendee (not self) is muted
  const isRemoteParticipantMuted = activeAttendees.some(
    (a: ActiveAttendee) =>
      a.externalUserId !== loggedInUser.id && remoteMuteMap[a.attendeeId],
  );
  const isMobileScreen = useIsMobile(true);

  const getSessionId = (attendees: Attendee[]) => {
    const therapist = attendees.find(attendee => attendee.isTherapist);

    return therapist ? therapist.sessionId : null;
  };

  const sessionId = getSessionId(attendees);

  const [priorityBasedDownlinkPolicy, setPriorityBasedDownlinkPolicy] =
    useState<VideoPriorityBasedPolicy | null>(null);
  const [remoteVideoSources, setRemoteVideoSources] = useState<VideoSource[]>(
    [],
  );
  const isInitFromWaitingRoom = Storage.getItem(
    'meetingInitiatedFromWaitingRoom',
  );

  useEffect(() => {
    const logger = new ConsoleLogger('PriorityDownlinkLogger', LogLevel.OFF);
    const newPolicy = new VideoPriorityBasedPolicy(logger);

    setPriorityBasedDownlinkPolicy(newPolicy);

    const activeInterval = setInterval(() => {
      sessionId && sendActiveUserStatus();
    }, 30000);

    return () => {
      Storage.removeItem('meetingInitiatedFromWaitingRoom');

      deviceControllerRef.current?.destroy();
      deviceControllerRef.current = null;
      clearInterval(activeInterval);
    };
  }, []);

  //  BACKGROUND BLUR IMPLEMENTATION
  useEffect(() => {
    toggleBackgroundBlur(blurBackground === 'light', true);
  }, [blurBackground]);

  const toggleBackgroundBlur = async (
    blurEnabled: boolean,
    fromBlurUpdate?: boolean,
  ) => {
    if (isBackgroundBlurSupported === undefined || !meetingManager.audioVideo)
      return;

    await toggleBackgroundBlurUtil(
      blurEnabled,
      videoDevice,
      (device: MediaDeviceInfo) =>
        createBackgroundBlurDevice(device) as Promise<any>,
      isBackgroundBlurSupported,
      isVideoEnabledFlag,
      isVideoEnabled,
      toggleVideo,
      meetingManager.audioVideo,
      fromBlurUpdate,
    );
  };

  useEffect(() => {
    if (localVideoResolution) {
      handleSetVideoResolution(localVideoResolution);
    }
  }, [localVideoResolution]);

  const handleSetVideoResolution = async (resolution: string) => {
    if (meetingManager.audioVideo) {
      await handleSetVideoResolutionUtil(resolution, meetingManager.audioVideo);
    }
  };

  const sendActiveUserStatus = async () => {
    try {
      if (isTherapist) {
        await getTherapistIsActiveInSession({ therapySessionID: sessionId });
      } else {
        await getClientIsActiveInSession({ therapySessionID: sessionId });
      }
    } catch (error) {
      console.error('Error sending active user status:', error);
    }
  };

  useEffect(() => {
    if ((isTherapist && !joinData) || (!isTherapist && chime_data === ''))
      return;
    initializeMeeting(
      isTherapist ? joinData?.attendeeInfo : chime_data?.attendeeInfo,
      isTherapist ? joinData?.meetingInfo : chime_data?.meetingInfo,
    );
  }, [joinData, chime_data]);

  const prepareAttendeeInfo = (
    activeRunningSessionData: ActiveRunningSessionData,
  ) => {
    const session = activeRunningSessionData.therapy_sessions[0];
    const attendees: Attendee[] = [];

    // Create client and therapist objects
    const clientAttendee = {
      id: session.clients[0].client_id,
      fullName: session.display_name,
      isTherapist: false,
      pronoun: session.clients[0].client_pronoun || '',
      image: session.clients[0].client_profile_picture || '',
      checkinQuestionnaireId: session.clients[0].checkin_questionnaire_id,
      clusterId: session.cluster_id,
      therapySessionId: session.id,
    };

    const therapistAttendee = {
      id: session.therapist_id,
      fullName: session.therapist_name,
      isTherapist: true,
      pronoun: loggedInUser.gender_pronoun || '',
      credentials: loggedInUser.credentials,
      image: session.therapist_profile_picture || '',
      sessionId: session.id,
    };

    // If logged in user is client, put therapist first in the array
    if (loggedInUser.user_type.includes(UserTypes.CLIENT)) {
      attendees.push(therapistAttendee, clientAttendee);
    } else {
      // For therapist, put client first in the array
      attendees.push(clientAttendee, therapistAttendee);
    }

    dispatch(setAttendees({ attendees }));
  };

  const fetchChimeData = async () => {
    try {
      if (isTherapist) {
        if (activeRunningSessionData?.therapy_sessions.length === 0) {
          const activeRunningSessions = await getActiveRunningSession();

          if (
            activeRunningSessions.therapy_sessions.length > 0 &&
            activeRunningSessions.therapy_sessions.length < 2
          ) {
            activeRunningSessions.therapy_sessions =
              activeRunningSessions.therapy_sessions.map((element: any) => {
                // to map the profile picture element to active session vertical
                element.photoUrl =
                  element?.clients[0]?.client_profile_picture || null;

                return element;
              });

            // Set attendees for therapist
            if (activeRunningSessions.therapy_sessions[0]) {
              await prepareAttendeeInfo(activeRunningSessions);
            }

            if (Object.entries(joinData).length === 0) {
              const therapistChimeData = await therapistStartSessionNewEndPoint(
                activeRunningSessions?.therapy_sessions[0].id,
              ); // start the session by therapist

              await dispatch(
                setJoinData({
                  joinData: therapistChimeData,
                  isReadyToJoin: true,
                }),
              );
            }

            dispatch(setActiveRunningSessionData(activeRunningSessions));
          } else {
            navigate('/');
          }
        } else if (
          activeRunningSessionData?.therapy_sessions.length > 0 &&
          Object.entries(joinData).length === 0
        ) {
          const therapistChimeData = await getChimeJoinData(
            activeRunningSessionData?.therapy_sessions[0]?.id,
          );

          // Set attendees for therapist using existing session
          if (activeRunningSessionData?.therapy_sessions[0]) {
            await prepareAttendeeInfo(activeRunningSessionData);
          }

          dispatch(setJoinData(therapistChimeData));
        }
      } else if (loggedInUser.user_type.includes(UserTypes.CLIENT)) {
        const activeRunningSessions = await getActiveRunningSession();

        if (
          activeRunningSessions.therapy_sessions.length > 0 &&
          activeRunningSessions.therapy_sessions.length < 2
        ) {
          await prepareAttendeeInfo(activeRunningSessions);
          dispatch(setActiveRunningSessionData(activeRunningSessions));

          const clientChimeData = await getChimeJoinData(
            activeRunningSessions?.therapy_sessions[0]?.id,
          );

          dispatch(setChimeSessionData(clientChimeData));
        } else {
          navigate('/');
        }

        Logger.log('activeRunningSessions', activeRunningSessions);
      }

      await initializeMeeting(
        isTherapist ? joinData?.attendeeInfo : chime_data?.attendeeInfo,
        isTherapist ? joinData?.meetingInfo : chime_data?.meetingInfo,
      );
    } catch (error) {
      console.error('Error in fetchChimeData:', error);
    }
  };

  // Add effect to fetch chime data if not available
  useEffect(() => {
    // Initial fetch
    !isInitFromWaitingRoom &&
      checkPermissions().then(hasPermissions => {
        if (hasPermissions) {
          fetchChimeData();
        }
      });
  }, []);

const attendeePresenceHandler = (
  attendeeId: string,
  present: boolean,
  externalUserId?: string,
) => {
  const attendeeName = attendees.find(
    (attendee: { id: string; clientId: string }) =>
      attendee.id === externalUserId || attendee.clientId === externalUserId,
  )?.fullName;

    setActiveAttendees((prevAttendees: ActiveAttendee[]) => {
      if (present) {
        if (
          !prevAttendees.some(
            (attendee: ActiveAttendee) => attendee.attendeeId === attendeeId,
          )
        ) {
          return [
            ...prevAttendees,
            {
              attendeeId,
              name: attendeeName || '',
              externalUserId: externalUserId || '',
            },
          ];
        }
      } else {
        return prevAttendees.filter(
          (attendee: ActiveAttendee) => attendee.attendeeId !== attendeeId,
        );
      }

      return prevAttendees;
    });

    // 🔊 NEW: subscribe/unsubscribe to volume indicator to track mute state
    if (!audioVideo) return;

    if (present) {
      audioVideo.realtimeSubscribeToVolumeIndicator(
        attendeeId,
        (_id, _volume, muted, _signalStrength) => {
          // muted can be null when it hasn't changed; ignore in that case
          if (muted === null) return;

          setRemoteMuteMap(prev => ({
            ...prev,
            [attendeeId]: muted,
          }));
        },
      );
    } else {
      audioVideo.realtimeUnsubscribeFromVolumeIndicator(attendeeId);

      setRemoteMuteMap(prev => {
        const updated = { ...prev };

        delete updated[attendeeId];

        return updated;
      });
    }
  };

  useEffect(() => {
    if (!audioVideo) return;

    const observer: AudioVideoObserver = {
      audioVideoDidStart: () => {
        handleInitialAudioVideoState();
      },
      connectionDidBecomePoor: () => {
        Logger.warn('Connection became poor');
      },
      connectionDidSuggestStopVideo: () => {
        Logger.warn('Suggested to stop video');
      },
      remoteVideoSourcesDidChange: (videoSources: VideoSource[]) => {
        setRemoteVideoSources(videoSources);
        updateDownlinkPreference();
      },
    };

    audioVideo.addObserver(observer);
    audioVideo.realtimeSubscribeToAttendeeIdPresence(attendeePresenceHandler);

    return () => {
      audioVideo.removeObserver(observer);
      audioVideo.realtimeUnsubscribeToAttendeeIdPresence(
        attendeePresenceHandler,
      );
    };
  }, [audioVideo]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateDownlinkPreference();
    }, 5000);

    return () => clearInterval(interval);
  }, [remoteVideoSources, metricsRef.current]);

  const updateDownlinkPreference = () => {
    if (!meetingManager.audioVideo) return;

    updateDownlinkPreferenceUtil(
      priorityBasedDownlinkPolicy,
      remoteVideoSources,
      metricsRef as React.MutableRefObject<MetricsData>,
    );
  };

  const handleInitialAudioVideoState = async () => {
    try {
      // Fall back to initial states if recent states don't exist
      const initialVideoState = Storage.getItem('meetingVideoEnabled');
      const initialAudioState = Storage.getItem('meetingAudioEnabled');

      // Handle video state
      const targetVideoState =
        initialVideoState !== null
          ? JSON.parse(initialVideoState)
          : isVideoEnabledFlag;

      // Only turn on video if targetVideoState is true AND current state is different
      if (targetVideoState !== isVideoEnabled) {
        if (videoDevice) {
          if (targetVideoState) {
            // Only start video if the target state is true
            await meetingManager.audioVideo?.startVideoInput(videoDevice);
            await toggleVideo();

            // Apply background blur if enabled
            if (blurBackground === 'light') {
              await toggleBackgroundBlur(true);
            }
          } else {
            // Make sure video is off if target state is false
            if (isVideoEnabled) {
              await meetingManager.audioVideo?.stopVideoInput();
              await toggleVideo();
            }
          }
        }
      }

      // Handle audio state
      const targetAudioState =
        initialAudioState !== null
          ? JSON.parse(initialAudioState)
          : isMicEnabled;

      const shouldBeMuted = !targetAudioState;

      if (shouldBeMuted && !muted) {
        await meetingManager.audioVideo?.stopAudioInput();
        await toggleMute();
      } else if (!shouldBeMuted && muted && micDevice) {
        await meetingManager.audioVideo?.startAudioInput(micDevice);
        await toggleMute();
      }

      // Only remove initial states after successful initialization
      // Keep lastVideoState and lastAudioState for persistence during page reloads
      // if (initialVideoState !== null) {
      //   Storage.removeItem('meetingVideoEnabled');
      // }

      // if (initialAudioState !== null) {
      //   Storage.removeItem('meetingAudioEnabled');
      // }
    } catch (error) {
      console.error('Error setting initial audio/video state:', error);
    }
  };

  const initializeMeeting = async (attendeeInfo: any, meetingInfo: any) => {
    try {
      if (attendeeInfo && meetingInfo) {
        const configuration = new MeetingSessionConfiguration(
          meetingInfo,
          attendeeInfo,
        );

        if (priorityBasedDownlinkPolicy) {
          configuration.videoDownlinkBandwidthPolicy =
            priorityBasedDownlinkPolicy;
        }

        // setAttendeeInfo(attendeeInfo);

        setMeetingInfo(meetingInfo);

        await meetingManager.join(configuration, { enableWebAudio: true });
        await startMeeting();
        await meetingManager.start();
        Storage.removeItem('meetingInitiatedFromWaitingRoom');
      }
    } catch (error) {
      console.error('Error initializing meeting:', error);
    }
  };

  // Function to start the meeting and initialize audio/video devices
  const startMeeting = async () => {
    try {
      if (meetingManager.audioVideo) {
        const deviceController = await startMeetingUtil(
          meetingManager.audioVideo,
          videoDevice,
          micDevice,
          isMicEnabled,
          muted,
          async () => {
            await toggleMute();
          },
          dispatch,
          setVideoDevice,
        );

        deviceControllerRef.current = deviceController;
      }
    } catch (error) {
      console.error('Error starting meeting:', error);
    }
  };

  const toggleContentShareCustom = async () => {
    await toggleContentShareCustomUtil(isLocalUserSharing, toggleContentShare);
  };

  const leaveMeeting = async () => {
    try {
      // Clean up saved states when explicitly leaving the meeting
      Storage.removeItem('meetingVideoEnabled');
      Storage.removeItem('meetingAudioEnabled');

      await audioVideo?.stopLocalVideoTile();
      videoDevice &&
        (await audioVideo?.stopVideoPreviewForVideoInput(videoDevice));

      await audioVideo?.stopAudioInput();

      await meetingManager.audioVideo?.stopLocalVideoTile();

      await meetingManager.audioVideo?.stopVideoInput();
      await meetingManager.audioVideo?.stopAudioInput();
      await meetingManager.audioVideo?.stop();

      await deviceControllerRef.current?.stopVideoInput();
      await deviceControllerRef.current?.stopAudioInput();

      await meetingManager.leave().then(async () => {
        await deviceControllerRef.current?.destroy();
        deviceControllerRef.current = null;
        dispatch(clearSession());

        setTimeout(async () => {
          if (loggedInUser.user_type.includes('CLIENT')) {
            if (sessionId) {
              await leaveSessionNewEndPoint(sessionId);
              navigate('/therapy/initquestions?fetchQuestionnaire=true');
            } else {
              navigate('/');
            }
          } else {
            await therapistEndSession(sessionId);

            navigate('/therapy/initquestions?fetchQuestionnaire=true');
          }
        }, 1000);
      });
    } catch (error) {
      console.error('Error leaving meeting:', error);
    }
  };

  const toggleAudioMute = async () => {
    try {
      if (muted) {
        if (micDevice) {
          // Store the current device state for persistence
          Storage.setItem(
            'lastMicDevice',
            JSON.stringify({
              deviceId: micDevice.deviceId,
              groupId: micDevice.groupId,
              kind: micDevice.kind,
              label: micDevice.label,
            }),
          );

          await meetingManager.audioVideo?.startAudioInput(micDevice);
        }

        await toggleMute();
        Storage.setItem('lastAudioState', JSON.stringify(true));
      } else {
        await meetingManager.audioVideo?.stopAudioInput();

        await toggleMute();
        Storage.setItem('lastAudioState', JSON.stringify(false));
      }
    } catch (error) {
      console.error('Error toggling audio mute:', error);
    }
  };

  // Save device states when they change
  useEffect(() => {
    if (micDevice) {
      Storage.setItem(
        meetingDevicePreference.LAST_MIC_DEVICE,
        JSON.stringify({
          deviceId: micDevice.deviceId,
          groupId: micDevice.groupId,
          kind: micDevice.kind,
          label: micDevice.label,
        }),
      );
    }
  }, [micDevice]);

  useEffect(() => {
    if (videoDevice) {
      Storage.setItem(
        meetingDevicePreference.LAST_VIDEO_DEVICE,
        JSON.stringify({
          deviceId: videoDevice.deviceId,
          groupId: videoDevice.groupId,
          kind: videoDevice.kind,
          label: videoDevice.label,
        }),
      );
    }
  }, [videoDevice]);

  // Load and restore device states
  useEffect(() => {
    const restoreDevices = async () => {
      try {
        const savedMicDevice = Storage.getItem(
          meetingDevicePreference.LAST_MIC_DEVICE,
        );
        const savedVideoDevice = Storage.getItem(
          meetingDevicePreference.LAST_VIDEO_DEVICE,
        );

        if (savedMicDevice && !micDevice) {
          const deviceData = JSON.parse(savedMicDevice);
          const deviceController = new DefaultDeviceController(
            new ConsoleLogger('DeviceController', LogLevel.OFF),
          );
          const audioInputs = await deviceController.listAudioInputDevices();
          const matchingDevice = audioInputs.find(
            device => device.deviceId === deviceData.deviceId,
          );

          if (matchingDevice) {
            dispatch(setMicDevice(matchingDevice));
          }
        }

        if (savedVideoDevice && !videoDevice) {
          const deviceData = JSON.parse(savedVideoDevice);
          const deviceController = new DefaultDeviceController(
            new ConsoleLogger('DeviceController', LogLevel.OFF),
          );
          const videoInputs = await deviceController.listVideoInputDevices();
          const matchingDevice = videoInputs.find(
            device => device.deviceId === deviceData.deviceId,
          );

          if (matchingDevice) {
            dispatch(setVideoDevice(matchingDevice));
          }
        }
      } catch (error) {
        console.error('Error restoring saved devices:', error);
      }
    };

    restoreDevices();
  }, []);

  const checkPermissions = async () => {
    const deviceStatus = await DevicePermission();

    if (
      !deviceStatus.isMicrophoneAlreadyCaptured ||
      !deviceStatus.isWebcamAlreadyCaptured
    ) {
      setShowPermissionModal(true);

      return false;
    }

    return true;
  };

  const handlePermissionGranted = () => {
    // Reload the meeting after permissions are granted
    window.location.reload();
  };

  return (
    <UserActivityProvider>
      <AudioVideoContext.Provider value={audioVideo}>
        <div className="chime-video-call">
          <PermissionModal
            show={showPermissionModal}
            onClose={() => setShowPermissionModal(false)}
            onPermissionGranted={handlePermissionGranted}
          />
          {meetingInfo && (
            <div>
              <div className="w-full h-full" style={{ height: '100vh' }}>
                <div
                  className={`video-content-container ${
                    sharingAttendeeId ? 'sharing-layout' : ''
                  }`}
                >
                  <VideoGrid
                    size={gridSize}
                    layout="standard"
                    className={sharingAttendeeId ? 'small-grid' : ''}
                  >
                    {tiles.length > 0 ? (
                        <div className="remote-video-wrapper">
                            <RemoteVideos />
                        </div>
                    ) : activeAttendees.filter(
                        (a: ActiveAttendee) =>
                          a.externalUserId !== loggedInUser.id,
                      ).length > 0 ? (
                      <NoRemoteVideoView className={'fluid-style'} />
                    ) : (
                      <h3
                        className="d-flex m-auto justify-content-center"
                        style={{ fontSize: '24px', color: 'white' }}
                      >
                        No Remote Participants Available
                      </h3>
                    )}
                    {isVideoEnabled && showSelfView ? (
                      <LocalVideo
                        nameplate="Me"
                        className={`${
                          gridSize > 1 || sharingAttendeeId
                            ? 'fluid-styles'
                            : 'static-styles'
                        } ${
                          showSelfView === true ? 'opacity-100' : 'opacity-0'
                        } local-video-tile`}
                      />
                    ) : (
                      <div
                        className={`${
                          sharingAttendeeId || gridSize > 1
                            ? 'fluid-styles pt-5 d-flex justify-content-center'
                            : 'static-styles'
                        }  bg-dark ${
                          showSelfView === true ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        {' '}
                        <NoRemoteVideoView selfUser={true} />
                      </div>
                    )}
                  </VideoGrid>
                  {isRemoteParticipantMuted && (
                    <div className="remote-muted-indicator">
                      {/* Replace with icon if you want */}
                     <img src={MicrophoneOff} alt="Muted" style={{width: '20px', height: '20px'}}/>

                    </div>
                  )}

                  {sharingAttendeeId && (
                    <div className="content-share-wrapper">
                      <ContentShare nameplate="Content share" />
                    </div>
                  )}
                </div>
                <IconsTray
                  isVideoEnabled={isVideoEnabled}
                  toggleVideo={toggleVideo}
                  muted={muted}
                  toggleAudioMute={toggleAudioMute}
                  toggleContentShareCustom={toggleContentShareCustom}
                  setShowModal={setShowModal}
                  leaveMeeting={leaveMeeting}
                  isTherapist={isTherapist}
                  isFromMeetingRoom={true}
                  isMobileScreen={isMobileScreen}
                />
              </div>
            </div>
          )}
        </div>
        <SettingsModal2 showModal={showModal} setShowModal={setShowModal} />
      </AudioVideoContext.Provider>
    </UserActivityProvider>
  );
};

export default MeetingRoom;
